/* Gerber carousel: scroll-snap track + prev/next + dots. Vanilla custom element.
   Several sections include this file, so the whole body is guarded to run once. */
if (!customElements.get('gerber-carousel')) {
class GerberCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-carousel-track]');
    if (!this.track) return;
    this.prev = this.querySelector('[data-carousel-prev]');
    this.next = this.querySelector('[data-carousel-next]');
    this.dotsEl = this.querySelector('[data-carousel-dots]');
    this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.pages = 0;
    this.ticking = false;
    this.paused = false;

    this.onScroll = () => {
      if (this.ticking) return;
      this.ticking = true;
      requestAnimationFrame(() => {
        this.ticking = false;
        this.update();
      });
    };
    this.onResize = () => {
      this.buildDots();
      this.update();
    };
    this.onKey = (e) => {
      if (e.target !== this.track) return;
      const rtl = getComputedStyle(this.track).direction === 'rtl';
      if (e.key === 'ArrowRight') this.step(rtl ? -1 : 1, true);
      else if (e.key === 'ArrowLeft') this.step(rtl ? 1 : -1, true);
      else return;
      e.preventDefault();
    };
    this.onBlockSelect = (e) => {
      if (this.contains(e.target)) e.target.scrollIntoView({ behavior: 'auto', block: 'nearest', inline: 'nearest' });
    };

    this.track.addEventListener('scroll', this.onScroll, { passive: true });
    this.track.addEventListener('keydown', this.onKey);
    this.addEventListener('shopify:block:select', this.onBlockSelect);
    window.addEventListener('resize', this.onResize);
    if (this.prev) this.prev.addEventListener('click', () => this.step(-1));
    if (this.next) this.next.addEventListener('click', () => this.step(1));

    this.buildDots();
    this.update();
    this.setupAutoplay();
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.onResize);
    clearInterval(this.timer);
  }

  get behavior() {
    return this.reduced.matches ? 'auto' : 'smooth';
  }

  get maxScroll() {
    return Math.max(0, this.track.scrollWidth - this.track.clientWidth);
  }

  get slideStep() {
    const slides = this.track.children;
    if (slides.length > 1) return Math.abs(slides[1].offsetLeft - slides[0].offsetLeft);
    return slides.length ? slides[0].offsetWidth : this.track.clientWidth;
  }

  get perView() {
    return Math.max(1, Math.floor((this.track.clientWidth + 1) / (this.slideStep || 1)));
  }

  /* dir: -1 or 1. single: move one slide (keyboard) instead of one page. */
  step(dir, single) {
    const distance = this.slideStep * (single ? 1 : this.perView);
    this.track.scrollBy({ left: dir * distance, behavior: this.behavior });
  }

  buildDots() {
    if (!this.dotsEl) return;
    const total = this.track.children.length;
    const pages = this.maxScroll > 1 ? Math.min(total, Math.ceil(total / this.perView)) : 1;
    if (pages === this.pages) return;
    this.pages = pages;
    this.dotsEl.textContent = '';
    for (let i = 0; i < pages; i++) {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'g-carousel__dot';
      b.setAttribute('aria-label', (this.dotsEl.dataset.label || '{n}').replace('{n}', i + 1).replace('{total}', pages));
      b.addEventListener('click', () => {
        const left = pages > 1 ? (i / (pages - 1)) * this.maxScroll : 0;
        this.track.scrollTo({ left, behavior: this.behavior });
      });
      this.dotsEl.appendChild(b);
    }
  }

  update() {
    const max = this.maxScroll;
    const x = Math.abs(this.track.scrollLeft);
    const scrollable = max > 2;
    if (this.prev) this.prev.disabled = scrollable && x <= 2;
    if (this.next) this.next.disabled = scrollable && x >= max - 2;
    if (this.dotsEl) {
      const active = this.pages > 1 && max > 0 ? Math.round((x / max) * (this.pages - 1)) : 0;
      Array.prototype.forEach.call(this.dotsEl.children, (d, i) => {
        if (i === active) d.setAttribute('aria-current', 'true');
        else d.removeAttribute('aria-current');
      });
    }
  }

  setupAutoplay() {
    const secs = parseInt(this.dataset.autoplay, 10);
    if (!secs || this.reduced.matches) return;
    const pause = () => {
      this.paused = true;
    };
    const resume = () => {
      this.paused = false;
    };
    ['mouseenter', 'focusin', 'touchstart'].forEach((t) => this.addEventListener(t, pause, { passive: true }));
    ['mouseleave', 'focusout'].forEach((t) => this.addEventListener(t, resume));
    this.timer = setInterval(() => {
      if (this.paused || document.hidden || (window.Shopify && window.Shopify.designMode)) return;
      if (this.track.scrollLeft >= this.maxScroll - 2) this.track.scrollTo({ left: 0, behavior: this.behavior });
      else this.step(1);
    }, secs * 1000);
  }
}

customElements.define('gerber-carousel', GerberCarousel);
}
